<template>
  <div class="dashboard-container feed-details-container">
    <div class="dashboard-body">
      <div class="d-flex justify-content-between mt-2">
        <h6>{{ $t('details.details') }}</h6>
        <h6>id: {{ currentFeed.id }}</h6>
      </div>
      <div class="dashboard-content" v-if="!isLoading">
        <div class="d-flex align-items-center justify-content-between mt-3">
          <div class="d-flex align-items-center">
            <div>
              <img class="icon-small" v-bind:src="matchFeedTemplateIcon(currentFeed.feedType)" />
            </div>
            <h5 class="mx-3">{{ currentFeed.name }}</h5>
          </div>
          <div v-if="currentFeed.logs.length > 0" class="d-flex">
            <div v-if="alerts.filter(alert => alert.level === 0).length > 0">
              <img class="icon mr-3" src="/app/images/icons/warning_danger.svg" />
            </div>
            <div v-else-if="alerts.filter(alert => alert.level === 1).length > 0">
              <img class="icon mr-3" src="/app/images/icons/warning.svg" />
            </div>
            <div v-else>
              <img class="icon" src="/app/images/icons/success.svg" />
            </div>
            <div>
              <p class="mb-2"><strong>{{ currentFeed.logs[0].productCount }} {{ $t('global.products') }} (<span v-if="currentFeed.logs[0].change > 0">+</span>{{ currentFeed.logs[0].change }})</strong></p>
              <p>{{ countHoursAgo(currentFeed.logs[0].date, new Date()) }} {{ $t('global.hours_ago') }}</p>
            </div>
          </div>
          <div v-else class="d-flex align-items-center">
            <img class="icon" src="/app/images/icons/warning.svg" />
            <div>
              <p class="mb-0 text-orange-dark">{{ $t('feeds.first_feed_update_warning') }}</p>
            </div>
          </div>
          <router-link :to="{ name: 'feeds', params: { id: $route.params.id }}">
            <button class="btn btn-large btn-gray-dark">
              <arrow-left-circle-icon></arrow-left-circle-icon>
              <span>{{ $t('details.feed_list_return') }}</span>
            </button>
          </router-link>
        </div>
        <div class="d-flex justify-content-between pt-5 feed-details-summary">
          <div v-if="alerts.filter(alert => alert.level === 0).length > 0" class="alert alert-danger mr-5 pb-5" role="alert">
            <h6 class="alert-heading">{{ $t('global.alerts2') }} {{ $t('global.alerts_critical')}} ({{ alerts.length }})</h6>
            <hr>
            <ul v-for="(alert, index) in alerts" v-bind:key="index">
              <li>
                <p class="mb-0">{{ alert.message }}</p>
              </li>
            </ul>
          </div>
          <div v-if="alerts.filter(alert => alert.level === 1).length > 0" class="alert alert-warning mr-5 pb-5" role="alert">
            <h6 class="alert-heading">{{ $t('global.alerts2') }} {{ $t('global.alerts_informative')}} ({{ alerts.length }})</h6>
            <hr>
            <ul v-for="(alert, index) in alerts" v-bind:key="index">
              <li>
                <p class="mb-0">{{ alert.message }}</p>
              </li>
            </ul>
          </div>
          <div v-if="alerts.length === 0" class="alert alert-success d-flex align-items-center mr-5 pt-5 pb-5" role="alert">
            <img class="icon" src="/app/images/icons/success.svg" />
            <p class="mb-0">{{  $t('feeds.feed_no_alert') }}</p>
          </div>
          <line-chart :chart-data="chartData" :options="chartOptions" :height="340"></line-chart>
        </div>
        <div class="pt-5">
          <table class="table table-striped table-bordered">
            <thead class="thead-light">
              <th>Status</th>
              <th>{{  $t('details.field') }}</th>
              <th>{{  $t('details.required') }}</th>
              <th>{{  $t('details.most_occurrences') }}</th>
              <th>{{  $t('details.type') }}</th>
            </thead>
            <tbody>
              <tr v-for="(mappingRule, index) in currentFeed.config.mappingRules" v-bind:key="index" v-bind:class="{'feed-field-row-warning': ((mappingRule.status === 'required') && (mappingRule.emptyValues > 0)) }">
                <td>
                  <img class="icon" v-if="(mappingRule.status === 'required') && (mappingRule.emptyValues > 0)" src="/app/images/icons/error.svg" />
                  <img class="icon" v-else src="/app/images/icons/success.svg" />
                </td>
                <td>{{ mappingRule.to }}</td>
                <td>
                  <p class="mb-0" v-if="mappingRule.status === 'required'">{{  $t('global.yes') }}</p>
                  <p class="mb-0" v-else>{{  $t('global.no') }}</p>
                </td>
                <td>
                  <p class="my-4 break-text" v-for="(occurrence, key) in mappingRule.occurrences" v-bind:key="key">
                    <span v-if="occurrence.value === ''" class="text-danger">{{  $t('global.empty') }}</span>
                    <span v-else>{{ occurrence.value }}</span>
                    <span>: {{ occurrence.count }}</span>
                  </p>
                </td>
                <td class="text-left">
                  <p class="text-success mb-2 break-text"><strong>{{  $t('details.correct_values') }} {{ currentFeed.logs[0].productCount - mappingRule.emptyValues}} ({{ (100 - (mappingRule.emptyValues / currentFeed.logs[0].productCount) * 100).toFixed(0) }}%)</strong></p>
                  <p v-if="mappingRule.emptyValues > 0" class="text-danger mb-0 break-text"><strong>{{  $t('details.empty_values') }} {{ mappingRule.emptyValues }} ({{ ((mappingRule.emptyValues / currentFeed.logs[0].productCount) * 100).toFixed(0) }}%)</strong></p>
                  <p v-else class="mb-0 break-text">{{  $t('details.empty_values') }} {{ mappingRule.emptyValues }}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else>
        <div class="spinner-border text-orange" role="status"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ArrowLeftCircleIcon } from 'vue-feather-icons'
import dates from '@/mixins/dates'
import axios from 'axios'
import LineChart from '../../Projects/Partials/LineChart'

export default {
  name: 'Details',

  data () {
    return {
      alerts: [],
      chartData: {},
      chartOptions: {},
      currentFeed: {},
      feedTemplates: [],
      feedTemplateFields: [],
      isLoadingAlerts: true,
      isLoadingTemplate: true,
      isLoadingTemplates: true
    }
  },

  mixins: [dates],

  props: {
    feed: { required: false, type: Object }
  },

  created () {
    if (this.feed === undefined) {
      this.currentFeed = this.$attrs.project.feeds.filter(feed => feed.id === this.$route.params.feedId)[0]
    } else {
      this.currentFeed = this.feed
    }
  },

  mounted () {
    this.prepareChartData()
    this.getFeedTemplates()
    this.getProjectAlerts(this.$attrs.project.id)
  },

  methods: {
    getFeedTemplates () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/feed/templates/')
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.feedTemplates = response.data
            this.isLoadingTemplates = false
            this.getFeedTemplate(this.currentFeed.feedType)
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    getFeedTemplate (feedTypeName) {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/feed/template/' + feedTypeName)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.feedTemplateFields = response.data.fields
            this.prepareFieldsData()
            this.isLoadingTemplate = false
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    prepareFieldsData () {
      this.currentFeed.config.mappingRules.forEach((mappingRule) => {
        const matchingTemplateField = this.feedTemplateFields.filter(field => field.name === mappingRule.to)[0]
        if (matchingTemplateField !== undefined) mappingRule.status = this.feedTemplateFields.filter(field => field.name === mappingRule.to)[0].status
        else mappingRule.status = 'optional'
        const occurrences = this.currentFeed.logs[0].occurrences
        let matchingOccurrences = {}
        Object.keys(occurrences).map(function (value) {
          if (value === mappingRule.to) matchingOccurrences = occurrences[value]
        })
        mappingRule.occurrences = Object.keys(matchingOccurrences).map(function (value) {
          return { value: value, count: matchingOccurrences[value] }
        }).sort((a, b) => (a.count < b.count) ? 1 : -1).slice(0, 5)
        const emptyValues = this.currentFeed.logs[0].emptyValues
        let matchingEmptyValues = {}
        Object.keys(emptyValues).map(function (value) {
          if (value === mappingRule.to) matchingEmptyValues = emptyValues[value]
        })
        mappingRule.emptyValues = matchingEmptyValues
      })
    },

    prepareChartData () {
      const logs = this.currentFeed.logs
      const labels = logs.slice(0, 7).map(function (log) { return log.date.substr(0, 10) })
      const values = logs.slice(0, 7).map(function (log) { return log.productCount })
      this.chartData = {
        labels: labels.reverse(),
        datasets: [
          {
            label: this.$t('feeds.feed_product_count'),
            backgroundColor: '#fbaf3d',
            data: values.reverse()
          }
        ]
      }

      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              stepSize: 500
            }
          }]
        }
      }
    },

    getProjectAlerts (projectId) {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/alerts/' + projectId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            if (response.data.feed !== undefined) this.alerts = response.data.feed.filter(feed => feed.feedId === this.currentFeed.id)
            this.isLoadingAlerts = false
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    matchFeedTemplateIcon (feedTemplateName) {
      return this.feedTemplates.filter(template => template.name === feedTemplateName)[0].imageUrl
    }
  },

  computed: {
    isLoading () {
      if ((this.isLoadingAlerts) || (this.isLoadingTemplate) || (this.isLoadingTemplates)) return true
      else return false
    },

    inactiveSources () {
      return this.sources.filter(source => source.active === false)
    }
  },

  components: { ArrowLeftCircleIcon, LineChart }
}
</script>
